/**
 * UI-side helpers for the wallet pass designer.
 *
 * The design contract — schema, defaults, binding registry and the
 * per-platform resolver — lives in @lemmonhq/blogic/wallet-design so the
 * editor, the tRPC router, DBOS and the customer app share one definition.
 * Only editor concerns belong here.
 */

import type {
  ApplePassStyle,
  WalletAppleRow,
  WalletPassKind,
  WalletPlatform,
} from "@lemmonhq/types";

export type {
  ApplePassStyle,
  WalletAppleRow,
  WalletAssetRef,
  WalletBarcodeSymbology,
  WalletBrandAssetSlot,
  WalletContentField,
  WalletLink,
  WalletPassDesign,
  WalletPassDesignDocument,
  WalletPassKind,
  WalletPlatform,
  WalletStampOverlay,
} from "@lemmonhq/types";

/** Which iOS preview chrome a merchant is looking at. */
export type PreviewTarget = "legacy" | "poster";

export const PASS_KINDS: WalletPassKind[] = [
  "membership",
  "coin",
  "reservation",
  "gift_card",
];

export const KIND_META: Record<
  WalletPassKind,
  { label: string; icon: string; appleStyle: ApplePassStyle }
> = {
  membership: { label: "Membership", icon: "loyalty", appleStyle: "storeCard" },
  coin: { label: "Coins", icon: "toll", appleStyle: "storeCard" },
  reservation: {
    label: "Reservation",
    icon: "event_seat",
    appleStyle: "eventTicket",
  },
  gift_card: { label: "Gift card", icon: "card_giftcard", appleStyle: "storeCard" },
};

/** The platforms a single design targets. Order is the switcher/badge order. */
export const WALLET_PLATFORMS: WalletPlatform[] = ["apple", "google", "browser"];

export const PLATFORM_META: Record<
  WalletPlatform,
  { label: string; icon: string }
> = {
  apple: { label: "Apple Wallet", icon: "wallet" },
  google: { label: "Google Wallet", icon: "account_balance_wallet" },
  browser: { label: "Browser", icon: "public" },
};

/** Apple's per-row limits; the shared schema enforces the same caps. */
export const APPLE_ROW_META: Record<
  WalletAppleRow,
  { label: string; max: number; hint: string }
> = {
  header: { label: "Header", max: 3, hint: "Top-right, beside the logo." },
  primary: { label: "Primary", max: 2, hint: "Largest text on the pass." },
  secondary: { label: "Secondary", max: 4, hint: "Row beneath the primary." },
  auxiliary: { label: "Auxiliary", max: 4, hint: "Smaller supporting row." },
  back: { label: "Back", max: 20, hint: "Reverse of the pass." },
};

export const APPLE_ROWS: WalletAppleRow[] = [
  "header",
  "primary",
  "secondary",
  "auxiliary",
  "back",
];

/** Sample coin balance used by the stamp preview. */
export const SAMPLE_COIN_BALANCE = 5;
